import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiUser, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';

interface Props {}

const outerBg =
  "relative min-h-screen flex items-center justify-center bg-black overflow-hidden";
const neonOrb =
  "absolute rounded-full blur-[90px] opacity-[0.12] pointer-events-none";
const orbTL =
  `${neonOrb} w-[430px] h-[430px] top-[-190px] left-[-220px] bg-white/6`;
const orbBR =
  `${neonOrb} w-[370px] h-[310px] bottom-[-140px] right-[-210px] bg-white/10`;
const trailLights =
  "absolute pointer-events-none left-1/2 top-0 w-[100vw] h-[50vw] -translate-x-1/2 z-0";
const glassCard =
  "relative z-10 w-full max-w-7xl flex flex-col md:flex-row rounded-[2.25rem] bg-black/90 backdrop-blur-3xl shadow-[0_10px_50px_0_rgba(40,80,255,0.10),0_2px_24px_0_rgba(40,80,255,0.03)] border-4 border-white/15 overflow-hidden before:absolute before:-top-8 before:left-1/2 before:-translate-x-1/2 before:w-28 before:h-12 before:bg-gradient-to-br before:from-blue-500/10 before:via-white/20 before:to-blue-500/10 before:rounded-b-3xl before:rotate-12 before:blur-[4px] before:content-['']";
const sidePanel =
  "hidden md:flex flex-col items-center justify-between bg-black/85 w-[45%] px-10 py-16 relative z-0 backdrop-blur-lg";
const logoRow =
  "flex items-center mb-7 drop-shadow-[0_1px_20px_rgba(255,255,255,0.07)]";
const tmsText =
  "font-black text-4xl md:text-5xl text-white tracking-tight";
const chip =
  "ml-4 px-3 py-1 text-xs rounded-full bg-gradient-to-br from-white/10 to-blue-700/15 text-blue-500 font-bold ring-2 ring-blue-500/40 shadow border border-blue-700/20";
const zenTruck =
  "animate-float-dark mt-6 mb-8 select-none text-[68px] drop-shadow-[0_8px_50px_#111]";
const dashline =
  "w-28 border-t border-white/15 mb-8";
const heroSlogan =
  "font-extrabold text-[1.5rem] leading-tight md:text-2xl text-white/90 tracking-tight text-center mb-3 animate-glow-dark";
const descText =
  "text-blue-300/90 text-center text-sm font-medium leading-snug mb-6 px-1";
const formWrap =
  "w-full flex flex-col px-0 md:px-0 py-12 md:py-16 justify-center items-center";

const neonToggle =
  "flex items-center justify-center gap-4 mb-10";

const toggleSwitchWrapper =
  "flex items-center justify-center relative w-[100px] h-10";

const toggleBtn =
  "relative flex items-center rounded-full w-[86px] h-10 bg-black/80 border-2 border-blue-400/40 shadow-inner transition focus:outline-none overflow-hidden ring-blue-500/40";
const toggleCircle =
  "absolute w-8 h-8 rounded-full shadow-xl transition-transform duration-400 z-10 top-1 left-1";
const toggleText =
  "text-xs font-bold tracking-wide transition-colors transition-opacity duration-300 select-none px-4 py-1 z-20";
const activeToggleText =
  "text-blue-500 opacity-100";
const inactiveToggleText =
  "text-white/50 opacity-60";

const labelStyle =
  "block text-[.94rem] font-bold mb-2 tracking-widest text-white/80";
const fieldWrap =
  "relative mb-7 w-[330px] sm:w-[95%]";

// --- OVERRIDE input and button styles below ---

// Remove border, shadow, gradient, background, etc. for input outer
const inputDiamondOuter =
  "relative flex items-center py-[2.5px] px-2 rounded-2xl bg-transparent";
// For input, no border, no shadow, plain white bg, dark text
const inputDiamondInner =
  "w-full relative rounded-2xl px-5 py-4 bg-white text-black font-semibold text-base placeholder:text-black/40 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed border-none shadow-none";
// Remove border/shadow from icons
const iconBtn =
  "flex items-center justify-center absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-transparent text-blue-400/80 text-xl transition cursor-pointer border-none shadow-none";
const passToggleBtn =
  "flex items-center justify-center absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-transparent text-blue-400 hover:text-black text-xl transition border-none shadow-none";

const errorAlert =
  "flex items-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-tr from-red-600/40 to-red-900/10 border border-red-500/50 text-red-400 font-semibold text-[15px] mb-5 animate-shake-dark shadow";

// Simple grey button for neonBtn
const neonBtn =
  "relative w-[95%] sm:w-[95%] py-4 rounded-2xl font-extrabold bg-gray-400 text-black group mt-6 focus:outline-none z-10";

const subtleLink =
  "text-xs text-blue-400 hover:text-blue-600 underline underline-offset-2 transition font-semibold";
const registerRow =
  "text-center text-sm font-medium text-white/80 mt-10";
const regLink =
  "text-blue-500 font-bold underline underline-offset-2 hover:text-red-500 ml-2 transition";
const zenFooter =
  "mt-10 text-center text-[14px] font-semibold text-white/30 tracking-wide select-none";

function Login(props: Props) {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const handleSwitch = () => {
    setIsRegister((prev) => !prev);
    setError('');
    setFormData({ username: '', password: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.username || !formData.password) {
      setError('All fields are required.');
      setLoading(false);
      return;
    }

    try {
      if (!isRegister) {
        // LOGIN FLOW
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/auth/login`,
          {
            email: formData.username,
            password: formData.password,
          }
        );
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
        }
        navigate('/');
      } else {
        // REGISTER FLOW
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/auth/register`,
          {
            name: formData.username,
            password: formData.password,
          }
        );
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
        }
        navigate('/');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          (isRegister
            ? "Registration failed. Please try again!"
            : "Login failed. Check your credentials!")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={outerBg}>
      <div className={orbTL}></div>
      <div className={orbBR}></div>
      <svg
        className={trailLights}
        viewBox="0 0 900 350"
        fill="none"
        aria-hidden="true"
      >
        <ellipse
          cx="200"
          cy="100"
          rx="150"
          ry="65"
          fill="white"
          fillOpacity="0.09"
        />
        <ellipse
          cx="680"
          cy="180"
          rx="120"
          ry="55"
          fill="white"
          fillOpacity="0.11"
        />
        <ellipse
          cx="475"
          cy="230"
          rx="190"
          ry="80"
          fill="white"
          fillOpacity="0.07"
        />
      </svg>
      <div className={glassCard + " shadow-lg"}>
        {/* Side/Left Panel */}
        <div className={sidePanel}>
          <div>
            <div className={logoRow}>
              <span className={tmsText}>TMS</span>
              <span className={chip}>Zen-G</span>
            </div>
            <span className={zenTruck}>🚛</span>
            <hr className={dashline} />
          </div>
          <div>
            <div className={heroSlogan}>
              <span className="inline-block bg-gradient-to-r from-blue-300/80 via-white/95 to-blue-400/50 bg-clip-text text-transparent animate-glow-dark">
                Ultra Zen Transport. <span className="text-blue-500">2026</span>
              </span>
            </div>
            <div className={descText}>
              A new dimension of logistics:<br />
              dark,
              <span className="text-blue-400 font-bold"> blue</span>,
              &nbsp;diamond-cut clarity.<br />
              Every sign-in, a moment of zen.<br />
            </div>
          </div>
        </div>

        {/* LOGIN / REGISTER FORM */}
        <form
          onSubmit={handleSubmit}
          className={formWrap + " bg-gradient-to-br from-blue-600/10 via-black/85 to-white/0 py-11 rounded-[1.5rem] shadow-xl border-t-4 border-white/5"}
          style={{
            boxShadow:
              "0 1px 3px #0af6f5bd, 0 5px 30px 10px #3250ff22",
            minWidth: 0,
            backdropFilter: "blur(3px)"
          }}
          tabIndex={0}
          aria-label={isRegister ? "Register form" : "Login form"}
        >
          {/* Top Diamond Accent */}
          <div className="flex flex-col items-center mb-7">
            <div className="mx-auto w-14 h-7 relative mb-2">
              <span className="absolute left-0 right-0 mx-auto top-0 w-14 h-[22px] rounded-b-[10px] bg-gradient-to-b from-blue-300/40 to-white/0 blur-[3px]" />
              <span className="block mx-auto w-10 h-5 rounded-b-xl bg-gradient-to-t from-blue-500/60 via-cyan-100/5 to-white/0 blur-[1px]" />
            </div>
            <h1 className="font-extrabold text-[2.1rem] sm:text-3xl text-white tracking-widest animate-glow-dark mb-1">
              <span className="bg-gradient-to-tr from-blue-200/70 via-white/70 to-blue-400/80 bg-clip-text text-transparent">
                {isRegister ? "Join Us" : "Welcome Back"}
              </span>
            </h1>
            <p className="text-blue-400/90 font-medium text-base mt-0">
              Experience diamond {isRegister ? "registration" : "login"}.
            </p>
          </div>

          {/* Toggle Switch for Login/Register - animated */}
          <div className={neonToggle + " mb-8"}>
            <div className={toggleSwitchWrapper}>
              {/* Animate the toggle "pill" */}
              <div className="absolute inset-0 flex items-center justify-between pointer-events-none z-0">
                {/* Empty for now, allows background effects */}
              </div>
              <button
                type="button"
                aria-pressed={isRegister}
                tabIndex={0}
                onClick={handleSwitch}
                className={toggleBtn + " group"}
                style={{
                  outline: isRegister ? '2px solid #3b82f680' : undefined,
                  boxShadow: isRegister
                    ? "0 0 23px 3px #3b82f6bb,0 2px 8px 3px #0ea5e922"
                    : "0 1px 4px 1px #1e293b33",
                  background:
                    'linear-gradient(90deg, #191b23 10%, #222f3e 85%)'
                }}
              >
                <span
                  className={
                    toggleText +
                    (isRegister ? inactiveToggleText : activeToggleText) +
                    " transition-all duration-300"
                  }
                  style={{
                    color: !isRegister ? '#3b82f6' : '#eee',
                  }}
                >
                  Login
                </span>
                <span
                  className={
                    toggleText +
                    (isRegister ? activeToggleText : inactiveToggleText) +
                    " transition-all duration-300"
                  }
                  style={{
                    color: isRegister ? '#3b82f6' : '#eee',
                  }}
                >
                  Register
                </span>
                <span
                  className={toggleCircle + " transition-transform"}
                  style={{
                    background: 'radial-gradient(circle at 55% 35%, #3b82f6 75%, #111 120%)',
                    boxShadow: isRegister
                      ? '0 0 23px 3px #3b82f6bb,0 2px 8px 3px #0ea5e922'
                      : '0 0 12px 1.5px #80c4ff77',
                    transform: isRegister ? 'translateX(42px)' : 'translateX(0)',
                    transition: 'all .32s cubic-bezier(.66,0,.34,1)'
                  }}
                />
              </button>
            </div>
            <span className="text-sm text-blue-600 font-extrabold select-none tracking-wide drop-shadow-md ml-3">
              {isRegister ? "Register" : "Login"}
            </span>
          </div>

          {error && (
            <div className={errorAlert} role="alert">
              <svg fill="none" viewBox="0 0 24 24" className="w-5 h-5" stroke="currentColor">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01" />
              </svg>
              {error}
            </div>
          )}

          {/* Input: Username */}
          <div className={fieldWrap + " group"}>
            <label htmlFor="username" className={labelStyle + " mb-3"}>
              <span>
                  <span className="text-blue-200 font-bold">Username</span>
              </span>
            </label>
            <div className={inputDiamondOuter}>
              <button
                type="button"
                tabIndex={-1}
                className={iconBtn}
                aria-label="Username"
                disabled
                style={{
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              >
                <FiUser />
              </button>
              <input
                type="text"
                autoComplete={isRegister ? "username" : "username"}
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className={inputDiamondInner + " pl-14 pr-3"}
                placeholder="Your user name"
                required
                disabled={loading}
                aria-required="true"
                aria-label="Username"
                spellCheck={false}
                style={{
                  letterSpacing: ".015em"
                }}
              />
            </div>
          </div>

          {/* Input: Password */}
          <div className={fieldWrap + " group"}>
            <label htmlFor="password" className={labelStyle + " mb-3"}>
              <span className="text-blue-200 font-bold">Password</span>
            </label>
            <div className={inputDiamondOuter}>
              <button
                type="button"
                tabIndex={-1}
                className={iconBtn}
                aria-label="Password"
                disabled
                style={{
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              >
                <FiLock />
              </button>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                autoComplete={isRegister ? "new-password" : "current-password"}
                value={formData.password}
                onChange={handleChange}
                className={inputDiamondInner + " pl-14 pr-10"}
                placeholder={isRegister ? "Choose a password" : "••••••••"}
                required
                aria-required="true"
                aria-label="Password"
                disabled={loading}
                spellCheck={false}
                style={{
                  letterSpacing: ".140em"
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className={passToggleBtn}
                tabIndex={0}
              >
                {showPassword ? (
                  <FiEyeOff className="" />
                ) : (
                  <FiEye className="" />
                )}
              </button>
            </div>
          </div>

          {/* Forgot Password & Version - hidden on register */}
          {!isRegister && (
            <div className="flex items-center justify-between mt-1 mb-5 w-[340px] sm:w-[360px] mx-auto">
              <Link
                to="/forgot-password"
                className={subtleLink + " font-bold"}
              >
                Forgot password?
              </Link>
              <span
                className="text-[11px] font-mono text-blue-500/60 select-none py-1 px-2 rounded"
              >
                V.2026
              </span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={neonBtn}
            tabIndex={0}
          >
            <span className="relative z-20">
              {loading
                ? (
                  <span className="flex items-center gap-2 justify-center">
                    <svg width="22" height="22" viewBox="0 0 22 22" className="animate-spin" fill="none">
                      <circle cx="11" cy="11" r="9" stroke="#3b82f6" strokeWidth="3" strokeDasharray="36 36" />
                    </svg>
                    {isRegister ? "Signing up" : "Signing in"}
                  </span>
                )
                : (
                  <span>
                    {isRegister ? (
                      <>
                        <span className="tracking-widest">REGISTER</span>
                        <span className="ml-1"> 🚀</span>
                      </>
                    ) : (
                      <>
                        <span className="tracking-widest">SIGN IN</span>
                        <span className="ml-1"> ✦</span>
                      </>
                    )}
                  </span>
                )
              }
            </span>
          </button>

          {/* Register & Footer (only show register-link if not already on register) */}
          {!isRegister && (
            <div className={registerRow}>
              <span>Not on board?</span>
              <Link to="/register" className={regLink}>
                Create Account
              </Link>
            </div>
          )}
          <div className={zenFooter + " mt-12"}>
            &copy; 2026 Zen-G TMS.<br />A project in transporting tranquility.
          </div>
        </form>
      </div>
      <style>
        {`
        @keyframes float-dark {
          0% { transform: translateY(0);}
          50% { transform: translateY(-12px);}
          100% { transform: translateY(0);}
        }
        .animate-float-dark { animation: float-dark 3.9s cubic-bezier(.3,.75,.48,1.05) infinite;}

        @keyframes glow-dark { 
          0%,100% { text-shadow: 0 0 20px #fff3,0 0px 9px #7dd3fc45;}
          47% { text-shadow: 0 0 24px #3b82f6cc,0 9px 40px #3266eb55;}
          75% { text-shadow: 0 0 15px #2563eb77, 0 1px 20px #fff1;}
        }
        .animate-glow-dark { animation: glow-dark 4.2s ease-in-out infinite alternate;}

        @keyframes shake-dark { 11%, 91% { transform: translateX(-1.5px);}
          20%, 81% { transform: translateX(2.9px);}
          30%, 51%, 71% { transform: translateX(-5px);}
          40%, 61% { transform: translateX(4px);}
        }
        .animate-shake-dark { animation: shake-dark 0.44s;}
        `}
      </style>
    </div>
  );
}

export default Login;